import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = ["data-handler-id", "draggable"]
const _hoisted_2 = {
  key: 0,
  class: "forbidden-icon",
  width: "16",
  height: "16",
  viewBox: "0 0 14 14",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}

import { computed, reactive, ref, unref, onUnmounted, onMounted, type CSSProperties } from 'vue'
import { useDrag, useDrop } from 'vue3-dnd'
import type { XYCoord, Identifier } from 'dnd-core'
import { toRefs, useThrottleFn } from '@vueuse/core'
import { DragItemTypes, BaseItemTypes } from './ItemTypes'

const EDGE_ZONE = 60;
// Краевая зона строки (maxHeight=28): верх/низ по E px — индикатор «в корень», центр — «внутрь».
const BETWEEN_ROOT_EDGE_PX = 6;
const BEYOND_BOOST = 2.5;
const MIN_SPEED = 1.5;
const MAX_SPEED = 12;

interface DragItem {
  index: number
  id: string
  type: string
  callbackParams: any
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DndListElement',
  props: {
    id: {},
    index: {},
    callbackParams: {},
    type: {},
    intoAllowed: { type: [Boolean, Function] },
    maxHeight: {},
    highlightCondition: { type: Function },
    hoverCondition: { type: Function },
    CtcDropCondition: { type: Function },
    CtrDropCondition: { type: Function },
    RtrDropCondition: { type: Function },
    RtcDropCondition: { type: Function },
    elementName: {},
    canDragCheck: { type: Function },
    betweenRootDropViaIndicator: { type: Boolean },
    canDropToRootCheck: { type: Function }
  },
  emits: ["changeParent", "cardMoved", "scrollViewport"],
  setup(__props: any, { emit: __emit }) {

/**
 * drag and drop item template
 */

let _scrollRaf: number | null = null;
let _scrollSpeed = 0;
let _scrollDir = 0;
let _scrollViewport: HTMLElement | null = null;
let _scrollLastUpdate = 0;

function edgeScrollTick() {
  if (!_scrollViewport || _scrollSpeed === 0 || Date.now() - _scrollLastUpdate > 150) {
    _scrollRaf = null;
    _scrollSpeed = 0;
    _scrollDir = 0;
    return;
  }
  _scrollViewport.scrollTop += _scrollDir * _scrollSpeed;
  _scrollRaf = requestAnimationFrame(edgeScrollTick);
}

function updateEdgeScroll(mouseY: number, viewport: HTMLElement | null) {
  if (!viewport) { stopEdgeScroll(); return; }
  const vr = viewport.getBoundingClientRect();
  const distTop = mouseY - vr.top;
  const distBottom = vr.bottom - mouseY;

  if (distTop < EDGE_ZONE) {
    _scrollDir = -1;
    const ratio = 1 - Math.max(0, distTop) / EDGE_ZONE;
    _scrollSpeed = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ratio;
    if (distTop < 0) _scrollSpeed *= BEYOND_BOOST;
  } else if (distBottom < EDGE_ZONE) {
    _scrollDir = 1;
    const ratio = 1 - Math.max(0, distBottom) / EDGE_ZONE;
    _scrollSpeed = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * ratio;
    if (distBottom < 0) _scrollSpeed *= BEYOND_BOOST;
  } else {
    _scrollSpeed = 0;
    _scrollDir = 0;
  }

  _scrollViewport = viewport;
  _scrollLastUpdate = Date.now();
  if (_scrollSpeed > 0 && !_scrollRaf) {
    _scrollRaf = requestAnimationFrame(edgeScrollTick);
  }
}

function stopEdgeScroll() {
  _scrollSpeed = 0;
  _scrollDir = 0;
  if (_scrollRaf) { cancelAnimationFrame(_scrollRaf); _scrollRaf = null; }
}

let _preventNativeActive = false;
function preventNativeDragOver(e: Event) {
  e.preventDefault();
  const dt = (e as DragEvent).dataTransfer;
  if (dt) dt.dropEffect = 'move';
}
function startPreventNativeDrag() {
  if (_preventNativeActive) return;
  _preventNativeActive = true;
  document.addEventListener('dragover', preventNativeDragOver, { capture: true });
  document.addEventListener('drop', preventNativeDragOver, { capture: true });
}
function stopPreventNativeDrag() {
  if (!_preventNativeActive) return;
  _preventNativeActive = false;
  document.removeEventListener('dragover', preventNativeDragOver, { capture: true });
  document.removeEventListener('drop', preventNativeDragOver, { capture: true });
}

// HTML5 drag не прокручивает scroll-container штатно — вручную крутим viewport списка
// на время drag-сессии: wheel (мышь) и клавиши ↑/↓/PageUp/PageDown (трекпад Mac).
let _dragScrollViewport: HTMLElement | null = null;
let _wheelListenerActive = false;
let _keyboardListenerActive = false;

function clearDragScrollViewportIfIdle() {
  if (!_wheelListenerActive && !_keyboardListenerActive) {
    _dragScrollViewport = null;
  }
}

function onWheelDuringDrag(e: WheelEvent) {
  if (!_dragScrollViewport) return;
  // Крутим только если курсор над viewport списка — не трогаем правую панель и шапку.
  const vr = _dragScrollViewport.getBoundingClientRect();
  if (e.clientY < vr.top || e.clientY > vr.bottom) return;
  if (e.clientX < vr.left || e.clientX > vr.right) return;

  _dragScrollViewport.scrollTop += e.deltaY;
  e.preventDefault();
}

function onKeyDuringDrag(e: KeyboardEvent) {
  if (!_dragScrollViewport) return;
  let delta = 0;
  if (e.key === 'ArrowDown') delta = 32;
  else if (e.key === 'ArrowUp') delta = -32;
  else if (e.key === 'PageDown') delta = _dragScrollViewport.clientHeight * 0.85;
  else if (e.key === 'PageUp') delta = -_dragScrollViewport.clientHeight * 0.85;
  else return;

  _dragScrollViewport.scrollTop += delta;
  e.preventDefault();
}

function startWheelScroll(viewport: HTMLElement | null) {
  if (!viewport) return;
  _dragScrollViewport = viewport;
  if (_wheelListenerActive) return;
  _wheelListenerActive = true;
  document.addEventListener('wheel', onWheelDuringDrag, { capture: true, passive: false });
}

function stopWheelScroll() {
  if (!_wheelListenerActive) return;
  _wheelListenerActive = false;
  document.removeEventListener('wheel', onWheelDuringDrag, { capture: true });
  clearDragScrollViewportIfIdle();
}

function startKeyboardScroll(viewport: HTMLElement | null) {
  if (!viewport) return;
  _dragScrollViewport = viewport;
  if (_keyboardListenerActive) return;
  _keyboardListenerActive = true;
  document.addEventListener('keydown', onKeyDuringDrag, { capture: true });
}

function stopKeyboardScroll() {
  if (!_keyboardListenerActive) return;
  _keyboardListenerActive = false;
  document.removeEventListener('keydown', onKeyDuringDrag, { capture: true });
  clearDragScrollViewportIfIdle();
}

function onGlobalDragEnd() {
  stopEdgeScroll();
  stopPreventNativeDrag();
  stopWheelScroll();
  stopKeyboardScroll();
}

let _dragEndCleanupListenerActive = false;
function ensureDragEndCleanupListener() {
  if (_dragEndCleanupListenerActive) return;
  _dragEndCleanupListenerActive = true;
  document.addEventListener('dragend', onGlobalDragEnd, { capture: true });
}

// Блокируем нативный copy-индикатор Chrome (зелёный «+» в круге на macOS):
// effectAllowed нужно ставить при dragstart, до завершения capture-фазы на document,
// поэтому слушатель регистрируется один раз глобально, а не на каждый drag.
let _effectAllowedListenerActive = false;
function ensureEffectAllowedListener() {
  if (_effectAllowedListenerActive) return;
  _effectAllowedListenerActive = true;
  document.addEventListener('dragstart', (e: Event) => {
    const dt = (e as DragEvent).dataTransfer;
    if (dt) dt.effectAllowed = 'move';
  }, { capture: true });
}

const props = __props

const betweenRootDropViaIndicator = computed(() => !!props.betweenRootDropViaIndicator)

const emit = __emit

const moveCard = (params: any) => {
  useThrottleFn((params: any) => { emit('cardMoved', params) }, 100)(params)
}

const throttledParentChange = useThrottleFn((params: any) => {
  emit('changeParent', params)
}, 1000, true, false)

const signalOnCTRDrop = (moved_item: string, new_parent: any, new_pos: number, 
  dragParams: any, hoverParams: any, dropPosition?: { x: number; y: number }) => {
  throttledParentChange({ item_id: moved_item, new_parent, new_pos, dragParams, hoverParams, dropPosition })
}

const card = ref<HTMLDivElement>()
const [dropCollect, drop] = useDrop<
  DragItem,
  void,
  { handlerId: Identifier | null, isOverCurrent: boolean }
>({
  accept: [ DragItemTypes.CARD_ROOT, DragItemTypes.CARD_CHILD ],
  collect(monitor) {
    return {
      handlerId: monitor.getHandlerId(),
      isOverCurrent: monitor.isOver({ shallow: true })
    }
  },
  hover(item: DragItem, monitor) {
    if (!card.value) {
      return
    }

    const offset = monitor.getClientOffset();
    if (offset && props.callbackParams.viewport) {
      updateEdgeScroll(offset.y, props.callbackParams.viewport);
    }

    // Временная блокировка source/target: без спец-иконок и без dnd-действий.
    const processingBlocked = !!item.callbackParams?.isProcessing || !!props.callbackParams?.isProcessing
    if (processingBlocked) {
      hoverMode.into = false
      hoverMode.forbidden = false
      hoverMode.between = false
      return
    }

    // allow or not any hovered-on item indication
    const highlightCond = props.highlightCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams,
      id: props.id
    })

    const isRootToRoot = props.RtrDropCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams,
      id: props.id,
    })

    // allow or not any action on hover (e.g. immediate reorder)
    const allowCond = props.hoverCondition({
      type: item.type,
      callbackParams: item.callbackParams
    }, {
      type: props.type,
      callbackParams: props.callbackParams
    })

    const hoverBoundingRect = card.value?.getBoundingClientRect()
    const rowH = props.maxHeight ?? (hoverBoundingRect.bottom - hoverBoundingRect.top)
    const clientOffset = monitor.getClientOffset()
    const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

    const hoverParams = { type: props.type, callbackParams: props.callbackParams, id: props.id }
    const intoAllowed = typeof props.intoAllowed === 'function'
      ? props.intoAllowed(item.callbackParams, hoverParams)
      : props.intoAllowed

    const isRootToRootHover = item.type === DragItemTypes.CARD_ROOT && props.type === DragItemTypes.CARD_ROOT
    const canRootDrop = props.canDropToRootCheck?.(item.callbackParams) ?? false

    hoverMode.between = false
    hoverMode.is_first = false

    if (betweenRootDropViaIndicator.value) {
      const inTopEdge = hoverClientY <= BETWEEN_ROOT_EDGE_PX
      const inBottomEdge = hoverClientY >= rowH - BETWEEN_ROOT_EDGE_PX

      if ((inTopEdge || inBottomEdge) && canRootDrop) {
        hoverMode.between = true
        hoverMode.is_first = inTopEdge
        hoverMode.into = false
        hoverMode.forbidden = false
        return
      }
    }

    // Режим «внутрь»: пунктир по всей строке (default) или центральная зона (опция on).
    if ((isRootToRootHover && highlightCond) || (!isRootToRootHover && highlightCond && intoAllowed)) {
      hoverMode.into = true
      hoverMode.forbidden = false
    } else {
      hoverMode.into = false
      const isFolder = props.callbackParams.elType === BaseItemTypes.FOLDER
      hoverMode.forbidden = isFolder
    }
  },

  drop(item: DragItem, monitor) {
    if (!monitor.isOver({ shallow: true })) return
    if (!!item.callbackParams?.isProcessing || !!props.callbackParams?.isProcessing) return
    const dropOffset = monitor.getClientOffset()
    const dropPosition = dropOffset ? { x: dropOffset.x, y: dropOffset.y } : undefined

    if (betweenRootDropViaIndicator.value && hoverMode.between) {
      if (!props.canDropToRootCheck?.(item.callbackParams)) return
      throttledParentChange({
        item_id: item.id,
        new_parent: null,
        new_pos: 0,
        dragParams: item.callbackParams,
        hoverParams: {},
        moveTargetKind: 'root',
        dropPosition,
      })
      return
    }

    if (!hoverMode.into) return
    signalOnCTRDrop(item.id, props.id, 0, item.callbackParams, props.callbackParams, dropPosition)
  }
})

const [collect, drag, connectDragPreview] = useDrag({
  type: props.type,
  item: () => {
    startPreventNativeDrag();
    startWheelScroll(props.callbackParams.viewport);
    startKeyboardScroll(props.callbackParams.viewport);
    return {
      id: props.id,
      index: props.index,
      type: props.type,
      callbackParams: props.callbackParams,
      elementName: props.elementName ?? '',
    }
  },
  canDrag: () => (
    !props.callbackParams?.isProcessing &&
    !props.callbackParams?.isEditing &&
    (props.canDragCheck ? props.canDragCheck() : true)
  ),
  collect: (monitor: any) => ({
    isDragging: monitor.isDragging(),
  }),
  end: () => {
    stopPreventNativeDrag();
    stopWheelScroll();
    stopKeyboardScroll();
  },
})

onMounted(() => {
  ensureEffectAllowedListener();
  ensureDragEndCleanupListener();
  const img = new Image()
  img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
  img.onload = () => connectDragPreview(img, { captureDraggingState: true })
})

const { handlerId, isOverCurrent } = toRefs(dropCollect)
const { isDragging } = toRefs(collect)
const hoverMode = reactive({ into: false, forbidden: false, between: false, is_first: false })
const opacity = computed(() => (unref(isDragging) ? 0 : 1))
const setRef = (el: HTMLDivElement) => {
  card.value = drag(drop(el)) as HTMLDivElement
}

// Реактивные размеры корневого wrapper-элемента — нужны для динамической генерации
// SVG-пунктира DnD-hover (см. dashStyle). viewBox с реальными пикселями устраняет
// растровое кэширование background-image и subpixel-различия между строками.
const wrapperEl = ref<HTMLDivElement | null>(null)
const widthPx = ref(0)
const heightPx = ref(0)
let _dashResizeObserver: ResizeObserver | null = null

function onWrapperRef(el: any) {
  wrapperEl.value = (el as HTMLDivElement) || null
  setRef(el as HTMLDivElement)
}

function makeDashSvg(w: number, h: number, colorHex: string): string {
  // Цвет в data URI кодируется как %23 вместо #.
  const c = colorHex.replace('#', '%23')
  // rx/ry захардкожены 8 — наружный border-radius 9 родителя обрезает «лапки»
  // SVG-stroke в углах (overflow: hidden), это согласованная пара, ломать нельзя.
  return `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25' viewBox='0 0 ${w} ${h}' preserveAspectRatio='none'%3e%3crect width='${w}' height='${h}' fill='none' rx='8' ry='8' stroke='${c}' stroke-width='2' stroke-dasharray='4 6' stroke-dashoffset='2' stroke-linecap='square' vector-effect='non-scaling-stroke'/%3e%3c/svg%3e")`
}

function makeDashLineSvg(w: number, colorHex: string): string {
  // Тот же пунктир, что у обводки into, но в формате одной горизонтальной линии.
  const c = colorHex.replace('#', '%23')
  return `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25' viewBox='0 0 ${w} 1' preserveAspectRatio='none'%3e%3cline x1='0' y1='0.5' x2='${w}' y2='0.5' stroke='${c}' stroke-width='1' stroke-dasharray='4 6' stroke-dashoffset='2' stroke-linecap='square' vector-effect='non-scaling-stroke'/%3e%3c/svg%3e")`
}

const dashStyle = computed<CSSProperties>(() => {
  const w = widthPx.value
  const h = heightPx.value
  if (!w || !h) return {}
  return {
    ['--dash-bg-into' as any]: makeDashSvg(w, h, '#3F884F'),
    ['--dash-line-into' as any]: makeDashLineSvg(w, '#3F884F'),
    ['--dash-bg-forbidden' as any]: makeDashSvg(w, h, '#FF5533'),
  }
})

onMounted(() => {
  // Первичный замер до первого ResizeObserver-callback, чтобы пунктир был доступен сразу.
  if (wrapperEl.value) {
    const r = wrapperEl.value.getBoundingClientRect()
    widthPx.value = Math.round(r.width)
    heightPx.value = Math.round(r.height)
  }
  if (typeof ResizeObserver !== 'undefined' && wrapperEl.value) {
    _dashResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      widthPx.value = Math.round(entry.contentRect.width)
      heightPx.value = Math.round(entry.contentRect.height)
    })
    _dashResizeObserver.observe(wrapperEl.value)
  }
})

onUnmounted(() => {
  stopEdgeScroll();
  stopPreventNativeDrag();
  stopWheelScroll();
  stopKeyboardScroll();
  if (_dashResizeObserver) { _dashResizeObserver.disconnect(); _dashResizeObserver = null; }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref: (onWrapperRef as any),
    class: _normalizeClass([ `wrapper`, `card${props.callbackParams.isRoot ? '--root' : ''}`,
      { 'wrapper--between-root': betweenRootDropViaIndicator.value,
        highlighted: _unref(isOverCurrent) && hoverMode.into,
        'highlighted--forbidden': _unref(isOverCurrent) && hoverMode.forbidden } ]),
    style: _normalizeStyle([{ opacity: opacity.value }, dashStyle.value]),
    "data-handler-id": _unref(handlerId),
    draggable: !props.callbackParams.isEditing && !props.callbackParams.isProcessing
  }, [
    (_unref(isOverCurrent) && hoverMode.forbidden)
      ? (_openBlock(), _createElementBlock("svg", _hoisted_2, _cache[0] || (_cache[0] = [
          _createStaticVNode("<g clip-path=\"url(#clip0_621_905)\" data-v-385a1508><path d=\"M2.87549 2.87524L11.1244 11.1247\" stroke=\"#D05A4F\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" data-v-385a1508></path><path d=\"M6.99984 12.8334C10.2215 12.8334 12.8332 10.2217 12.8332 7.00008C12.8332 3.77842 10.2215 1.16675 6.99984 1.16675C3.77818 1.16675 1.1665 3.77842 1.1665 7.00008C1.1665 10.2217 3.77818 12.8334 6.99984 12.8334Z\" stroke=\"#D05A4F\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" data-v-385a1508></path></g><defs data-v-385a1508><clipPath id=\"clip0_621_905\" data-v-385a1508><rect width=\"14\" height=\"14\" fill=\"white\" data-v-385a1508></rect></clipPath></defs>", 2)
        ])))
      : _createCommentVNode("", true),
    _renderSlot(_ctx.$slots, "default"),
    (betweenRootDropViaIndicator.value && _unref(isOverCurrent) && !_unref(isDragging) && hoverMode.between)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(['indicator', { first: hoverMode.is_first }])
        }, null, 2))
      : _createCommentVNode("", true)
  ], 14, _hoisted_1))
}
}

})